import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ClientContactType } from '@econtractor/types';
import * as apiClient from 'api/apiClients';
import { useToast } from 'context/ToastContext';
import {
    createContactLocalKey,
    normalizeContactValue,
    toContactRow,
    validateContactValue,
    type ClientContactRow,
    type ContactsAdapter,
} from './clientContactTypes';

export type { ContactsAdapter } from './clientContactTypes';
export type ClientContactsMode = 'view' | 'edit';

const clientContactsAdapter: ContactsAdapter = {
    list: (ownerId) => apiClient.getClientContacts(ownerId),
    add: (ownerId, contact) => apiClient.addClientContact(ownerId, contact),
    update: (ownerId, contactId, contactValue) =>
        apiClient.updateClientContact(ownerId, contactId, contactValue),
    remove: (ownerId, contactId) => apiClient.deleteClientContact(ownerId, contactId),
};

interface UseClientContactsParams {
    clientId?: number | null;
    ownerId?: number | null;
    adapter?: ContactsAdapter;
    mode: ClientContactsMode;
    drafts?: ClientContactRow[];
    onDraftsChange?: (next: ClientContactRow[]) => void;
}

const useClientContacts = ({
    clientId = null,
    ownerId = null,
    adapter,
    mode,
    drafts = [],
    onDraftsChange,
}: UseClientContactsParams) => {
    const { t } = useTranslation(['Clients', 'common']);
    const { showToast } = useToast();
    const reqIdRef = useRef(0);

    const contactsAdapter = adapter ?? clientContactsAdapter;
    const adapterRef = useRef(contactsAdapter);
    adapterRef.current = contactsAdapter;
    const resolvedOwnerId = ownerId ?? clientId ?? null;
    const isDraft = mode === 'edit' && (!resolvedOwnerId || resolvedOwnerId === 0);
    const canEdit = mode === 'edit';

    const [contacts, setContacts] = useState<ClientContactRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editingKey, setEditingKey] = useState<string | null>(null);
    const [editValue, setEditValue] = useState('');
    const [newType, setNewType] = useState<ClientContactType>('phone');
    const [newValue, setNewValue] = useState('');

    const showValidationError = useCallback(
        (key: ReturnType<typeof validateContactValue>) => {
            if (!key) return;
            showToast(t(`contacts.validation.${key}`), 'danger');
        },
        [showToast, t],
    );

    const reload = useCallback(async () => {
        if (isDraft || !resolvedOwnerId) {
            setContacts([]);
            setLoading(false);
            return;
        }

        const reqId = ++reqIdRef.current;
        setLoading(true);

        const data = await adapterRef.current.list(resolvedOwnerId);
        if (reqId !== reqIdRef.current) return;

        if (!data) {
            setContacts([]);
            setLoading(false);
            showToast(t('contacts.errors.load'), 'danger');
            return;
        }

        setContacts(data.map(toContactRow));
        setLoading(false);
    }, [isDraft, resolvedOwnerId, showToast, t]);

    useEffect(() => {
        if (isDraft) {
            setContacts([]);
            setLoading(false);
            setEditingKey(null);
            setEditValue('');
            setNewValue('');
            return;
        }
        void reload();
    }, [isDraft, reload, resolvedOwnerId]);

    const visibleContacts = isDraft ? drafts : contacts;

    const addContact = useCallback(async () => {
        const validationKey = validateContactValue(newType, newValue);
        if (validationKey) {
            showValidationError(validationKey);
            return;
        }

        const contactValue = normalizeContactValue(newType, newValue);

        if (isDraft) {
            onDraftsChange?.([
                ...drafts,
                {
                    ID: 0,
                    clientId: 0,
                    contactType: newType,
                    contactValue,
                    localKey: createContactLocalKey(),
                },
            ]);
            setNewValue('');
            return;
        }

        if (!resolvedOwnerId) return;
        setSaving(true);
        const result = await adapterRef.current.add(resolvedOwnerId, { contactType: newType, contactValue });
        setSaving(false);
        if (!result.ok) {
            showToast(t('contacts.errors.save'), 'danger');
            return;
        }
        setContacts((prev) => [...prev, toContactRow(result.contact)]);
        setNewValue('');
    }, [
        drafts,
        isDraft,
        newType,
        newValue,
        onDraftsChange,
        resolvedOwnerId,
        showToast,
        showValidationError,
        t,
    ]);

    const startEdit = useCallback((row: ClientContactRow) => {
        setEditingKey(row.localKey);
        setEditValue(row.contactValue);
    }, []);

    const cancelEdit = useCallback(() => {
        setEditingKey(null);
        setEditValue('');
    }, []);

    const saveEdit = useCallback(async () => {
        if (!editingKey) return;
        const row = visibleContacts.find((item) => item.localKey === editingKey);
        if (!row) {
            cancelEdit();
            return;
        }

        const validationKey = validateContactValue(row.contactType, editValue);
        if (validationKey) {
            showValidationError(validationKey);
            return;
        }

        const contactValue = normalizeContactValue(row.contactType, editValue);

        if (isDraft) {
            onDraftsChange?.(
                drafts.map((item) => (item.localKey === editingKey ? { ...item, contactValue } : item)),
            );
            cancelEdit();
            return;
        }

        if (!resolvedOwnerId || !row.ID) return;
        setSaving(true);
        const result = await adapterRef.current.update(resolvedOwnerId, row.ID, contactValue);
        setSaving(false);
        if (!result.ok) {
            showToast(t('contacts.errors.save'), 'danger');
            return;
        }
        setContacts((prev) =>
            prev.map((item) => (item.localKey === editingKey ? toContactRow(result.contact) : item)),
        );
        cancelEdit();
    }, [
        cancelEdit,
        drafts,
        editValue,
        editingKey,
        isDraft,
        onDraftsChange,
        resolvedOwnerId,
        showToast,
        showValidationError,
        t,
        visibleContacts,
    ]);

    const removeContact = useCallback(
        async (row: ClientContactRow) => {
            if (isDraft) {
                onDraftsChange?.(drafts.filter((item) => item.localKey !== row.localKey));
                if (editingKey === row.localKey) cancelEdit();
                showToast(t('contacts.deleteSuccess'), 'success');
                return;
            }

            if (!resolvedOwnerId || !row.ID) return;
            setSaving(true);
            const stat = await adapterRef.current.remove(resolvedOwnerId, row.ID);
            setSaving(false);
            if (!stat) {
                showToast(t('common:messError'), 'danger');
                return;
            }
            setContacts((prev) => prev.filter((item) => item.localKey !== row.localKey));
            if (editingKey === row.localKey) cancelEdit();
            showToast(t('contacts.deleteSuccess'), 'success');
        },
        [cancelEdit, drafts, editingKey, isDraft, onDraftsChange, resolvedOwnerId, showToast, t],
    );

    return {
        contacts: visibleContacts,
        loading,
        saving,
        canEdit,
        editingKey,
        editValue,
        newType,
        newValue,
        setNewType,
        setNewValue,
        setEditValue,
        addContact,
        startEdit,
        cancelEdit,
        saveEdit,
        removeContact,
    };
};

export default useClientContacts;
